import { useFeedback } from "@taunggyi-plus/auth";
import { useCallback, useEffect, useState } from "react";
import { useAuth, usePagedRecords } from "@taunggyi-plus/auth";
import {
  Card,
  RecordFormDialog,
  EmptyState,
  ManagementListToolbar,
  PaginatedList,
  RECORDS_PER_PAGE,
  ServerPagination,
  StatusPill,
} from "@taunggyi-plus/design-system";

const statusLabels: Record<string, string> = {
  all: "All statuses",
  open: "Open",
  in_review: "In review",
  resolved: "Resolved",
  rejected: "Rejected",
};
type Order = {
  id: string;
  service: string;
  org: string;
  label: string;
  status: string;
  finished: boolean;
};
type Case = {
  id: string;
  service: string;
  order_id: string;
  kind: string;
  status: string;
  reason: string;
  resolution: string;
  refund_minor: number;
  refund_reference: string;
};
export function ServiceCare({
  mode,
  orderOnly = false,
}: {
  mode: "customer" | "provider" | "admin";
  orderOnly?: boolean;
}) {
  const params = new URLSearchParams(window.location.search);
  const orderId = params.get("support");
  const { client } = useAuth();
  const [reviewsVersion, setReviewsVersion] = useState(0);
  const [orders, setOrders] = useState<Order[]>([]),
    [favourites, setFavourites] = useState<string[]>([]),
    [favouriteProviders, setFavouriteProviders] = useState<
      { id: string; name: string; kind: string }[]
    >([]),
    [selection, setSelection] = useState(() =>
      orderOnly ? `${params.get("service")}:${orderId}` : "",
    ),
    [search, setSearch] = useState(""),
    [status, setStatus] = useState("all"),
    [message, setMessage] = useFeedback(),
    [busy, setBusy] = useState(false);
  const records = usePagedRecords<Case>("cases", search, status);
  const load = useCallback(async () => {
    if (!client) return;
    const [o, f] = await Promise.all([
      client.rpc("my_service_orders"),
      client.rpc("my_favourite_providers"),
    ]);
    const error = [o, f].find((r) => r.error)?.error;
    if (error) throw new Error(error.message);
    setOrders((o.data ?? []) as Order[]);
    setFavourites(((f.data ?? []) as { id: string }[]).map((r) => r.id));
    setFavouriteProviders((f.data ?? []) as { id: string; name: string; kind: string }[]);
  }, [client]);
  useEffect(() => {
    const timer = setTimeout(() => void load().catch((e) => setMessage(e.message)), 0);
    return () => clearTimeout(timer);
  }, [load, setMessage]);
  async function action(name: string, args: Record<string, unknown>) {
    if (!client || busy) return;
    setBusy(true);
    try {
      const r = await client.rpc(name, args);
      if (r.error) throw new Error(r.error.message);
      await load();
      records.refresh();
      setReviewsVersion((v) => v + 1);
      setMessage("Saved.");
    } catch (e) {
      setMessage(e instanceof Error ? e.message : "Could not save.");
    } finally {
      setBusy(false);
    }
  }
  const order = orders.find((o) => `${o.service}:${o.id}` === selection),
    filtered = records.items.filter((r) => !orderOnly || r.order_id === orderId);
  return (
    <div className="role-more-page service-care">
      <a className="more-back-link" href={orderOnly ? "/orders" : "/more"}>
        ← Back
      </a>

      {message && (
        <p className="form-message" role="status">
          {message}
        </p>
      )}
      {mode === "customer" && (
        <Card className="management-card role-more-detail-card service-care__card">
          <h3>Your orders & reviews</h3>
          <label className="field">
            Your order
            <select value={selection} onChange={(e) => setSelection(e.target.value)}>
              <option value="">Choose an order</option>
              {orders
                .filter((o) => !orderOnly || o.id === orderId)
                .map((o) => (
                  <option key={`${o.service}:${o.id}`} value={`${o.service}:${o.id}`}>
                    {o.label} · {o.service} · {o.id.slice(0, 8)} · {o.status}
                  </option>
                ))}
            </select>
          </label>
          {order && (
            <>
              <button
                className="button button--secondary"
                disabled={busy}
                onClick={() =>
                  void action("set_provider_favourite", {
                    org: order.org,
                    favourite: !favourites.includes(order.org),
                  })
                }
              >
                {favourites.includes(order.org)
                  ? "Remove favourite"
                  : "Save provider to favourites"}
              </button>
              <RecordFormDialog title="Request order support">
                {" "}
                <form
                  className="form-grid service-care__form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const f = new FormData(e.currentTarget);
                    void action("open_service_case", {
                      service: order.service,
                      order_id: order.id,
                      kind: f.get("kind"),
                      reason: f.get("reason"),
                    });
                  }}
                >
                  <label className="field">
                    Help needed
                    <select name="kind">
                      <option value="support">Support</option>
                      <option value="cancellation">Cancellation assistance</option>
                      <option value="refund">Refund request</option>
                    </select>
                  </label>
                  <label className="field">
                    Reason
                    <textarea name="reason" minLength={5} maxLength={1000} required />
                  </label>
                  <button className="button" disabled={busy}>
                    Open case
                  </button>
                </form>
              </RecordFormDialog>
              {order.finished && (
                <RecordFormDialog title="Write a review">
                  {" "}
                  <form
                    className="form-grid service-care__form"
                    onSubmit={(e) => {
                      e.preventDefault();
                      const f = new FormData(e.currentTarget);
                      void action("review_completed_service", {
                        service: order.service,
                        order_id: order.id,
                        rating: Number(f.get("rating")),
                        comment: f.get("comment"),
                      });
                    }}
                  >
                    <ProviderReviews key={`${order.org}:${reviewsVersion}`} org={order.org} />
                    <h3>Verified service review</h3>
                    <label className="field">
                      Rating
                      <select name="rating">
                        {[5, 4, 3, 2, 1].map((n) => (
                          <option key={n}>{n}</option>
                        ))}
                      </select>
                    </label>
                    <label className="field">
                      Review
                      <textarea name="comment" required minLength={3} maxLength={1000} />
                    </label>
                    <button className="button" disabled={busy}>
                      Save review
                    </button>
                  </form>
                </RecordFormDialog>
              )}
            </>
          )}
          {!orderOnly && (
            <>
              {" "}
              <h3>Favourite providers</h3>
              <PaginatedList
                items={favouriteProviders}
                label="Favourite provider pages"
                renderItem={(o) => (
                  <div className="service-care__favourite" key={o.id}>
                    <a
                      href={`/services?type=${({ hotel: "hotel", merchant: "food", delivery_fleet: "delivery", taxi_group: "taxi" } as Record<string, string>)[o.kind]}`}
                    >
                      {o.name}
                    </a>{" "}
                    <button
                      type="button"
                      className="button button--secondary"
                      disabled={busy}
                      onClick={() =>
                        void action("set_provider_favourite", { org: o.id, favourite: false })
                      }
                    >
                      Remove favourite
                    </button>
                  </div>
                )}
              />
            </>
          )}
        </Card>
      )}
      <ManagementListToolbar
        query={search}
        onQueryChange={(query) => {
          records.setPage(0);
          setSearch(query);
        }}
        searchLabel="Search cases"
        filters={[
          {
            label: "Status",
            value: status,
            onChange: (value) => {
              records.setPage(0);
              setStatus(value);
            },
            options: Object.entries(statusLabels).map(([value, label]) => ({ value, label })),
          },
        ]}
        resultCount={records.total}
        onClear={() => {
          records.setPage(0);
          setSearch("");
          setStatus("all");
        }}
      />
      <div className="service-care__cases" aria-busy={records.loading}>
        {(records.error || records.loading) && (
          <Card className="management-card role-more-detail-card service-care__card">
            <p role="status">{records.error || "Loading cases…"}</p>
            {records.error && (
              <button className="button button--secondary" type="button" onClick={records.refresh}>
                Try again
              </button>
            )}
          </Card>
        )}
        {!records.error && !records.loading && filtered.length === 0 && (
          <Card className="service-care__card">
            <EmptyState
              icon={
                <svg
                  viewBox="0 0 24 24"
                  width="24"
                  height="24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  aria-hidden="true"
                >
                  <path d="M5 4h14v13H9l-4 4V4Z" />
                  <path d="M8 8h8M8 12h5" />
                </svg>
              }
              title={search || status !== "all" ? "No matching cases" : "No support cases yet"}
              description={
                search || status !== "all"
                  ? "Try another search or clear your filters."
                  : "Support requests and refund updates will appear here."
              }
            />
          </Card>
        )}
        <PaginatedList
          pagination="external"
          items={filtered}
          resetKey={`${search}:${status}`}
          label="Support case pages"
          renderItem={(c) => (
            <Card
              className="management-card role-more-detail-card service-care__card service-care__case"
              key={c.id}
            >
              <div className="service-care__case-heading">
                <div>
                  <h3>
                    {c.service} · {c.kind}
                  </h3>
                  <p>Order #{c.order_id.slice(0, 8)}</p>
                </div>
                <StatusPill
                  tone={
                    c.status === "resolved"
                      ? "success"
                      : ["open", "in_review"].includes(c.status)
                        ? "warning"
                        : "neutral"
                  }
                >
                  {statusLabels[c.status] ?? c.status}
                </StatusPill>
              </div>
              <p>{c.reason}</p>
              {c.resolution && <p>{c.resolution}</p>}
              {c.refund_minor > 0 && (
                <p>
                  Refund recorded: {(c.refund_minor / 100).toLocaleString()} MMK ·{" "}
                  {c.refund_reference}
                </p>
              )}
              {mode !== "customer" && ["open", "in_review"].includes(c.status) && (
                <form
                  className="form-grid service-care__form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const f = new FormData(e.currentTarget);
                    void action("resolve_service_case", {
                      case_id: c.id,
                      status: f.get("status"),
                      response: f.get("response"),
                      refund: Math.round(Number(f.get("refund") ?? 0) * 100),
                      reference: f.get("reference") ?? "",
                    });
                  }}
                >
                  <label className="field">
                    Status
                    <select name="status">
                      {(mode === "admin"
                        ? ["in_review", "resolved", "rejected"]
                        : ["in_review"]
                      ).map((s) => (
                        <option key={s} value={s}>
                          {statusLabels[s] ?? s}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="field">
                    Response
                    <textarea name="response" minLength={5} maxLength={2000} required />
                  </label>
                  {mode === "admin" && c.kind === "refund" && (
                    <>
                      <label className="field">
                        Confirmed refund paid (MMK)
                        <input type="number" name="refund" min={0} step="0.01" defaultValue={0} />
                        <small>Record a verified payment. This does not send money.</small>
                      </label>
                      <label className="field">
                        Refund payment reference
                        <input name="reference" maxLength={240} />
                      </label>
                    </>
                  )}
                  <button className="button" disabled={busy}>
                    Save response
                  </button>
                </form>
              )}
            </Card>
          )}
        />
      </div>
      {!records.error && records.total > RECORDS_PER_PAGE && (
        <ServerPagination
          page={records.page}
          total={records.total}
          onPage={records.setPage}
          loading={records.loading}
        />
      )}
    </div>
  );
}

function ProviderReviews({ org }: { org: string }) {
  const { client } = useAuth();
  const [page, setPage] = useState(0),
    [data, setData] = useState<{
      total: number;
      average: number | null;
      items: { id: string; rating: number; comment: string }[];
    }>({ total: 0, average: null, items: [] }),
    [error, setError] = useFeedback("", "error");
  useEffect(() => {
    if (!client) return;
    let active = true;
    void client.rpc("provider_review_page", { org, page }).then((r) => {
      if (!active) return;
      if (r.error) setError(r.error.message);
      else
        setData((old) => {
          const next = r.data as typeof data;
          return page === 0
            ? next
            : {
                ...next,
                items: [
                  ...(old?.items ?? []).filter((o) => !next.items.some((n) => n.id === o.id)),
                  ...next.items,
                ],
              };
        });
    });
    return () => {
      active = false;
    };
  }, [client, org, page, setError]);
  return (
    <section className="service-care__reviews">
      <h3>Verified customer reviews</h3>
      <p>
        {data.average ?? "No ratings yet"}
        {data.average ? " / 5" : ""}
      </p>
      {error && <p role="status">{error}</p>}
      {data.items.map((r) => (
        <p key={r.id}>
          <strong>{r.rating} / 5</strong> · {r.comment}
        </p>
      ))}
      <ServerPagination page={page} total={data.total} onPage={setPage} />
    </section>
  );
}
